"use client";

import { useRef, useState } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(useGSAP);

type NavigationTabsProps = {
  setCurrentSection: (section: string) => void;
  currentSection: string;
  ready?: boolean;
};

const labelMap: Record<string, { default: string; alternate: string }> = {
  yesterday: { default: "Yesterday", alternate: "Work" },
  today: { default: "today", alternate: "Now" },
  about: { default: "about", alternate: "About" },
  tomorrow: { default: "tomorrow", alternate: "Services" },
};

const DEFAULT_BACK_TARGET = "today";

export function NavigationTabs({ currentSection, setCurrentSection, ready = false }: NavigationTabsProps) {
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);
  // Section the user came from when they entered "about" — used by [ BACK ].
  const previousSection = useRef<string>(DEFAULT_BACK_TARGET);
  const containerRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (!ready) return;

      // Query document-wide so the mobile contact button (rendered inside
      // TodaySection, outside this component's tree) animates with the rest.
      // Bypass useGSAP's selector scoping by going through the DOM directly.
      const items = Array.from(
        document.querySelectorAll<HTMLElement>(".nav-reveal")
      );
      if (items.length === 0) return;

      const mm = gsap.matchMedia();
      mm.add(
        {
          reduceMotion: "(prefers-reduced-motion: reduce)",
          normal: "(prefers-reduced-motion: no-preference)",
        },
        (ctx) => {
          if (ctx.conditions?.reduceMotion) {
            gsap.set(items, { autoAlpha: 1, y: 0 });
            return;
          }

          requestAnimationFrame(() => {
            gsap.fromTo(
              items,
              { autoAlpha: 0, y: 10 },
              {
                autoAlpha: 1,
                y: 0,
                duration: 0.6,
                ease: "power2.out",
                stagger: 0.05,
                delay: 0.35,
                clearProps: "transform",
              }
            );
          });
        }
      );
    },
    { scope: containerRef, dependencies: [ready] }
  );

  const handleNavigate = (section: string) => {
    if (section !== "about" && currentSection !== section) {
      // Direct nav to a non-about tab — remember it so [ BACK ] can return here.
      previousSection.current = section;
    }
    setCurrentSection(section);
  };

  const handleAboutClick = () => {
    if (currentSection === "about") {
      const target = previousSection.current === "about"
        ? DEFAULT_BACK_TARGET
        : previousSection.current;
      setCurrentSection(target);
      return;
    }
    previousSection.current = currentSection;
    setCurrentSection("about");
  };

  const getLabel = (section: string) => {
    // Show alternate label if hovered or selected
    if (hoveredTab === section || currentSection === section) {
      return labelMap[section].alternate;
    }
    return labelMap[section].default;
  };

  const aboutLabel =
    currentSection === "about"
      ? "BACK"
      : hoveredTab === "about"
      ? labelMap.about.alternate
      : labelMap.about.default;
  return (
    <div ref={containerRef} className="absolute z-50 bottom-4 w-full flex lg:flex-row flex-col-reverse items-center lg:gap-0 gap-4 lg:items-end justify-between px-4 lg:px-14 lg:pb-8">
    <div className="flex items-center lg:w-full lg:items-center lg:justify-start flex-row gap-2 lg:gap-4">
        <div className="w-full lg:ml-[72px] flex flex-row justify-center gap-2 lg:gap-4">
        <button
        onClick={() => handleNavigate("yesterday")}
        onMouseEnter={() => setHoveredTab("yesterday")}
        onMouseLeave={() => setHoveredTab(null)}
        className={`nav-reveal opacity-0 text-nav px-2 flex-none lg:px-3 w-24 lg:w-33 py-3 rounded-sm transition-colors ${
            currentSection === "yesterday"
            ? "bg-[#0009FF] text-black"
            : "text-white/80 bg-[#2C3030] hover:text-black hover:bg-[#FFFFFF]"
        }`}
        >
        [ {getLabel("yesterday")} ]
        </button>
        <button
        onClick={() => handleNavigate("today")}
        onMouseEnter={() => setHoveredTab("today")}
        onMouseLeave={() => setHoveredTab(null)}
        className={`nav-reveal opacity-0 text-nav px-2 flex-none lg:px-3 w-24 lg:w-30 py-3 rounded-sm transition-colors ${
            currentSection === "today"
            ? "bg-[#0009FF] text-black"
            : "text-white/80 bg-[#2C3030] hover:text-black hover:bg-[#FFFFFF]"
        }`}
        >
        [ {getLabel("today")} ]
        </button>
        <button
        onClick={() => handleNavigate("tomorrow")}
        onMouseEnter={() => setHoveredTab("tomorrow")}
        onMouseLeave={() => setHoveredTab(null)}
        className={`nav-reveal opacity-0 text-nav px-2 flex-none lg:px-2 w-24 lg:w-33 py-3 lg:py-2 rounded-sm transition-colors ${
            currentSection === "tomorrow"
            ? "bg-[#0009FF] text-black"
            : "text-white/80 bg-[#2C3030] hover:text-black hover:bg-[#FFFFFF]"
        }`}
        >
        [ {getLabel("tomorrow")} ]
        </button>
        </div>
    </div>

    <div className="flex-row justify-center items-center flex gap-2 lg:gap-4">
        {/* <p className="lg:hidden flex font-[lekton] text-sm uppercase">LET&apos;S BUILD TOMORROW</p> */}
        <button
        onClick={handleAboutClick}
        onMouseEnter={() => setHoveredTab("about")}
        onMouseLeave={() => setHoveredTab(null)}
        className={`nav-reveal opacity-0 ${currentSection === "today" ? "block" : "hidden"} lg:block text-nav px-2 lg:px-3 w-24 lg:w-30 flex-none py-3 rounded-sm transition-colors ${
            currentSection === "about"
            ? "bg-[#0009FF] text-black"
            : "text-white/80 bg-[#2C3030] hover:text-black hover:bg-[#FFFFFF]"
        }`}
        >
        [ {aboutLabel} ]
        </button>
        <a href="mailto:JOSEPH@TMRWPR.COM" className="nav-reveal opacity-0 hidden lg:block text-nav bg-white whitespace-nowrap text-black px-4 py-3 rounded-sm hover:bg-[#0009FF] transition-colors">
            [ contact ]
        </a>
    </div>
    </div>
  );
}
